/**
 * Generic supervised subprocess pool.
 *
 * A `WorkerPool<Handle>` manages a small set of long-lived child OS processes
 * and the scheduling around them: lazy spawn up to `size`, idle reuse, a FIFO
 * waiter queue when saturated, a readiness handshake before any work, one
 * in-flight unit per child, a per-call wall-clock timeout that SIGKILLs and
 * respawns a hung child, and a graceful drain. It is the protocol-agnostic core
 * the cpu-offload, ffmpeg, and browser pools all share.
 *
 * Everything that differs between those three (what the child runs, and how a
 * unit of work is dispatched and its result returned) is pushed into a
 * {@link WorkerDriver}. The pool owns *process lifecycle and scheduling*; the
 * driver owns *the wire*.
 *
 * Why processes, not threads: only an OS signal (`SIGKILL`) can interrupt a
 * non-yielding synchronous JS runaway — `worker_threads.terminate()` and Effect
 * interruption both fail to — so a hung unit of work is reclaimed by killing its
 * whole process.
 *
 * Children also carry a per-child **RSS cap** (`limits.rssBytes`): a dedicated
 * sub-second monitor SIGKILLs and respawns any child that exceeds it (only the
 * offending child, not the whole worker), the only memory enforcement available
 * on Fargate, where there are no per-child cgroups. The monitor is armed only
 * when a cap is set.
 *
 * Two checkout models share this core. **Per-call** (`run`): acquire → run →
 * release, sub-second, bounded by a wall-clock timeout. **Session-lease**
 * (`lease`): a child is held across many calls (a browser across a workflow run);
 * a heartbeat extends its TTL, a missed heartbeat reclaims it, and on release the
 * driver's optional `healthy`/`reset` hooks recycle or wipe it before reuse. A
 * driver's `onBreach` policy decides whether reclaiming a *live* leased child
 * SIGKILLs it now or defers the kill until the holder releases — so the core does
 * not SIGKILL a live session out from under its consumer, except the two backstops
 * that always hard-kill: a lease-TTL lapse and a whole-pool `drain`.
 *
 * This file is the pool *coordinator* only. The driver-facing contract lives in
 * {@link ./types.js}, the typed errors in {@link ./errors.js}, the RSS-cap
 * monitor in {@link ./rss-monitor.js}, and the spawn-surface narrowing in
 * {@link ./child-handle.js}. See `./README.md` for the file-ownership map that
 * keeps later pool features from all landing here.
 *
 * @module worker-pool
 */

import { spawn as nodeSpawn } from 'node:child_process';
import { type BrowserLogger, consoleLogger } from '../logger.js';
import {
  WorkerPoolError,
  WorkerPoolLeaseExpiredError,
  WorkerPoolOomError,
  WorkerPoolTimeoutError,
} from './errors.js';
import type { PooledChild, Waiter } from './bookkeeping.js';
import { WorkerPoolLeaseCoordinator } from './lease-coordinator.js';
import { DEFAULT_READY_TIMEOUT_MS, WorkerPoolRssMonitor } from './rss-monitor.js';
import {
  acquireScheduledWorker,
  rejectScheduledWaiters,
  releaseScheduledWorker,
} from './scheduler.js';
import {
  type WorkerPoolResourceCoordinatorOptions,
  WorkerPoolResourceCoordinator,
} from './resource-coordinator.js';
import type { CallSample } from './resource-sampler.js';
import { WorkerPoolSaturationTracker } from './saturation.js';
import { spawnWorkerChild } from './spawn-lifecycle.js';
import type {
  Lease,
  WorkerDriver,
  WorkerPoolLeaseOptions,
  WorkerPoolOptions,
  WorkerPoolRetirementReason,
  WorkerPoolStats,
} from './types.js';

// Re-export the public surface so the `worker-pool/worker-pool.js` import
// boundary cpu-offload (and the tests) rely on stays a single entry point even
// though the contract/errors/handle pieces now live in sibling files.
export type { ChildHandle } from './child-handle.js';
export type {
  WorkerEntry,
  DriverHooks,
  WorkerDriver,
  Lease,
  WorkerPoolStats,
  WorkerPoolOptions,
  WorkerPoolLeaseOptions,
  WorkerPoolLeaseTelemetryContext,
  WorkerPoolLeaseGraceCounts,
  WorkerPoolLeaseGraceOutcome,
  WorkerPoolRetirementCounts,
  WorkerPoolRetirementReason,
} from './types.js';
export type { ResourcePercentiles, WorkerPoolResourceStats } from './resource-sampler.js';
export type { WaitPercentiles, WorkerPoolSaturationStats } from './saturation.js';
export {
  WorkerPoolError,
  WorkerPoolLeaseExpiredError,
  WorkerPoolOomError,
  WorkerPoolTimeoutError,
} from './errors.js';
export { DEFAULT_LEASE_TTL_MS } from './lease-coordinator.js';

/**
 * Supervised pool of disposable child processes. Generic over the driver's
 * `Handle` — the typed dispatch surface a readied child exposes.
 */
export class WorkerPool<Handle> {
  private readonly size: number;
  private readonly driver: WorkerDriver<Handle>;
  private readonly spawn: typeof nodeSpawn;
  private readonly logger: BrowserLogger;
  private readonly readyTimeoutMs: number;
  /** Clock for the circuit-breaker's lifetime math (injectable so tests can drive it deterministically). */
  private readonly now: () => number;
  /** Monotonic clock for the lease-sweep stall detector (injectable; see WorkerPoolOptions.monotonicNow). */
  private readonly monotonicNow: () => number;
  private readonly rssMonitor: WorkerPoolRssMonitor<Handle>;
  private readonly leaseCoordinator: WorkerPoolLeaseCoordinator<Handle>;
  private readonly resourceCoordinator: WorkerPoolResourceCoordinator<Handle>;
  private readonly saturation: WorkerPoolSaturationTracker;

  private readonly children = new Set<PooledChild<Handle>>();
  private readonly idle: PooledChild<Handle>[] = [];
  private readonly waiters: Waiter<Handle>[] = [];
  private draining = false;

  private readonly retirements = { timeout: 0, oom: 0, lease_expired: 0, crash: 0 };

  constructor(options: WorkerPoolOptions<Handle>) {
    this.size = Math.max(1, options.size);
    this.driver = options.driver;
    this.spawn = options.spawn ?? nodeSpawn;
    this.logger = options.logger ?? consoleLogger;
    this.readyTimeoutMs = options.driver.limits.readyTimeoutMs ?? DEFAULT_READY_TIMEOUT_MS;
    this.now = options.now ?? Date.now;
    this.monotonicNow = options.monotonicNow ?? (() => performance.now());
    this.saturation = new WorkerPoolSaturationTracker({ now: this.now });
    this.rssMonitor = new WorkerPoolRssMonitor({
      children: this.children,
      rssBytes: options.driver.limits.rssBytes,
      readRssBytes: options.readRssBytes,
      rssCheckIntervalMs: options.rssCheckIntervalMs,
      logger: this.logger,
      now: this.now,
      isDraining: () => this.draining,
      onOom: (worker, error) => this.retire(worker, error),
      getTotalOomKills: () => this.retirements.oom,
    });
    this.leaseCoordinator = new WorkerPoolLeaseCoordinator({
      driver: this.driver,
      children: this.children,
      idle: this.idle,
      logger: this.logger,
      now: this.now,
      monotonicNow: this.monotonicNow,
      defaultLeaseTtlMs: options.defaultLeaseTtlMs,
      leaseCheckIntervalMs: options.leaseCheckIntervalMs,
      isDraining: () => this.draining,
      releaseIdle: (worker) => this.release(worker),
      retire: (worker, error, force) => this.retire(worker, error, force),
      killWorker: (worker) => this.killWorker(worker),
      spawnReplacement: () => this.spawnReplacement(),
    });
    const resourceOptions: WorkerPoolResourceCoordinatorOptions<Handle> = {
      children: this.children,
      now: this.now,
      isDraining: () => this.draining,
      logger: this.logger,
      ...(options.measureResourcesMs ? { measureResourcesMs: options.measureResourcesMs } : {}),
      ...(options.readProcStat ? { readProcStat: options.readProcStat } : {}),
      ...(options.readPssBytes ? { readPssBytes: options.readPssBytes } : {}),
      ...(options.listProcPids ? { listProcPids: options.listProcPids } : {}),
    };
    this.resourceCoordinator = new WorkerPoolResourceCoordinator(resourceOptions);
  }

  /**
   * Turn the per-driver calibration sampler on/off (or change its cadence) live —
   * so flipping `worker.pool.measure-resources-ms` on a running worker takes effect
   * on the already-warm pool instead of only on the next process start. `0` stops
   * sampling (and the `/proc` reads); a positive cadence arms the leased-session
   * poll. Idempotent: a repeated same-value call is a no-op, so a 15 s config
   * refresh does not discard the accumulated samples by rebuilding.
   */
  setMeasureResourcesMs(ms: number): void {
    this.resourceCoordinator.setMeasureResourcesMs(ms);
  }

  /**
   * Acquire a ready child, run `fn` against its `Handle` with a wall-clock
   * deadline, and release the child on settle. On timeout (or a child crash /
   * driver-fatal mid-call) the child is SIGKILLed and replaced, and the call
   * rejects with a typed error. A *value* rejection from `fn` (a protocol-level
   * error from a healthy child) releases the child unharmed.
   */
  async run<R>(
    fn: (handle: Handle) => Promise<R>,
    opts?: { timeoutMs?: number; label?: string }
  ): Promise<R> {
    if (this.draining) throw new WorkerPoolError('pool is shutting down');
    const worker = await this.acquire();
    const pid = worker.child.pid;
    const timeoutMs = opts?.timeoutMs ?? this.driver.limits.timeoutMs;
    return new Promise<R>((resolve, reject) => {
      let settled = false;
      // Filled by the per-call /proc read below; read back by finish() on the
      // healthy settle. Undefined unless sampling is armed (and the read succeeds).
      let callSample: CallSample | undefined;
      const timer = setTimeout(() => this.onTimeout(worker), timeoutMs);
      // Settle the call exactly once. `release` distinguishes the healthy path
      // (return the child to the pool) from retire (the child is already gone).
      const finish = (action: () => void, release: boolean): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        worker.current = null;
        action();
        // Record only healthy settles: a retired child was SIGKILLed, so its
        // end-of-call /proc read would fail anyway and any partial is misleading.
        if (release) void this.resourceCoordinator.endCall(pid, callSample);
        if (release) this.release(worker);
      };
      // Install the fail/timeout path BEFORE the per-call /proc read below. That
      // read is async, so a child exit or driver-fatal in its window fires
      // retire(); with worker.current already set, retire rejects this call via
      // fail() instead of stranding the just-acquired slot (which would otherwise
      // never be released, retried, or replaced — the later timeout's retire is a
      // no-op once worker.retired is set).
      worker.current = {
        timeoutMs,
        label: opts?.label,
        // Called by retire (timeout / crash / fatal): the child is being killed,
        // so reject without releasing it back to the pool.
        fail: (err) => finish(() => reject(err), false),
      };
      const handle = worker.handle;
      if (handle === undefined) {
        // Unreachable — acquire() only yields a readied child — but retire (not a
        // bare reject) so a regression can't quietly strand the slot.
        this.retire(worker, new WorkerPoolError('acquired worker has no handle'));
        return;
      }
      // Per-call calibration (§4.5): snapshot CPU/clock before the work starts so
      // vCPU is measured over the exact call window. No-op (undefined) unless
      // sampling is armed; a rejected read is swallowed so it never strands the call.
      void this.resourceCoordinator
        .beginCall(pid)
        .then((sample) => {
          callSample = sample;
          // If retire already fired (a crash/fatal between acquire and here, or
          // during the /proc read above), the call is already settled — don't
          // dispatch to a dead child and leave its response promise dangling.
          return settled ? Promise.reject(new WorkerPoolError('worker retired')) : fn(handle);
        })
        .then(
          (value) => finish(() => resolve(value), true),
          // `fn` rejected on its own (e.g. the worker returned an error frame).
          // The child is healthy; release it. If retire already fired, `settled`
          // is set and this is a no-op.
          (err: unknown) => finish(() => reject(err), true)
        );
    });
  }

  /**
   * Session-lease checkout: acquire a ready child and hold it across many calls
   * (a browser across a workflow run), instead of returning it on each `run`. The
   * returned {@link Lease} exposes the child's `Handle`; `heartbeat()` pushes the
   * TTL out and `release()` returns the child (recycling it through the driver's
   * `healthy`/`reset` hooks). A leaseholder that stops heartbeating past its TTL is
   * reclaimed by the expiry sweep. Leases ride the same lazy-spawn + FIFO waiter
   * scheduling as `run`, so a lease beyond `size` queues until a slot frees.
   */
  async lease(opts?: WorkerPoolLeaseOptions): Promise<Lease<Handle>> {
    if (this.draining) throw new WorkerPoolError('pool is shutting down');
    const worker = await this.acquire();
    const handle = worker.handle;
    if (handle === undefined) {
      // Unreachable — acquire() only yields a readied child — but retire (not a
      // bare throw) so a regression can't quietly strand the slot.
      this.retire(worker, new WorkerPoolError('acquired worker has no handle'), true);
      throw new WorkerPoolError('acquired worker has no handle');
    }
    return this.leaseCoordinator.createLease(worker, handle, opts);
  }

  stats(): WorkerPoolStats {
    const resourceStats = this.resourceCoordinator.stats();
    return {
      ...(this.driver.name !== undefined ? { driver: this.driver.name } : {}),
      size: this.children.size,
      idle: this.idle.length,
      busy: this.children.size - this.idle.length,
      leased: this.leaseCoordinator.countLeased(),
      queued: this.waiters.length,
      // `kills` is a projection of `retirements` minus the lease-expiry bucket --
      // one counter, two views. It predates `retirements` and keeps the
      // ExpensivePoolKills metric family's shape.
      kills: {
        timeout: this.retirements.timeout,
        oom: this.retirements.oom,
        crash: this.retirements.crash,
      },
      saturation: this.saturation.stats(),
      retirements: { ...this.retirements },
      leaseGrace: this.leaseCoordinator.graceStats(),
      ...this.rssMonitor.stats(),
      ...(resourceStats ? { resourceStats } : {}),
    };
  }

  /**
   * Reject queued callers, SIGKILL every child, and refuse further work. A worker
   * shutdown is a hard stop regardless of any `onBreach: 'drain'` policy — a job
   * whose leased child dies is re-run by the stall-detector (spec D6), so live
   * leases are SIGKILLed here too rather than waited on.
   */
  async drain(): Promise<void> {
    this.draining = true;
    this.rssMonitor.stop();
    this.resourceCoordinator.stop();
    this.leaseCoordinator.stop();
    rejectScheduledWaiters(
      this.waiters,
      new WorkerPoolError('pool shutting down'),
      this.saturation
    );
    for (const worker of this.children) {
      worker.retired = true;
      worker.lease = null;
      worker.current?.fail(new WorkerPoolError('pool shutting down'));
      this.killWorker(worker);
    }
    this.children.clear();
    this.idle.length = 0;
  }

  // ── internals ──────────────────────────────────────────────────────────

  private acquire(): Promise<PooledChild<Handle>> {
    return acquireScheduledWorker({
      idle: this.idle,
      waiters: this.waiters,
      canSpawn: () => this.children.size < this.size,
      spawn: () => this.spawnChild(),
      saturation: this.saturation,
    });
  }

  private release(worker: PooledChild<Handle>): void {
    if (worker.retired || this.draining) return;
    releaseScheduledWorker({
      worker,
      idle: this.idle,
      waiters: this.waiters,
      saturation: this.saturation,
    });
  }

  private spawnChild(replacement = false): Promise<PooledChild<Handle>> {
    return spawnWorkerChild({
      driver: this.driver,
      spawn: this.spawn,
      children: this.children,
      replacement,
      readyTimeoutMs: this.readyTimeoutMs,
      now: this.now,
      logger: this.logger,
      isDraining: () => this.draining,
      queuedWaiterCount: () => this.waiters.length,
      onPoolBecameActive: () => {
        this.rssMonitor.start();
        this.resourceCoordinator.start();
      },
      onSpawnFailedWithQueuedWaiters: () => this.spawnReplacement(),
      retire: (worker, error, force) => this.retire(worker, error, force),
      killWorker: (worker) => this.killWorker(worker),
    });
  }

  private onTimeout(worker: PooledChild<Handle>): void {
    if (!worker.current) return;
    // Only the per-call `run` path arms a timeout (`worker.current` is set), and a
    // child on that path is never leased — so the `onBreach='drain'` defer branch in
    // retire is a no-op here regardless. A hung call means the child must die now.
    this.retire(worker, new WorkerPoolTimeoutError(worker.current.timeoutMs));
  }

  /**
   * SIGKILL a worker's process. For a `killProcessGroup` driver the child leads
   * its own process group (spawned `detached`), so we signal the **negative pid**
   * to kill the group — reaping any grandchildren the child spawned (a runner's
   * browser) instead of orphaning them. Falls back to a single-pid kill when the
   * group send fails (group already gone, or an injected test spawn whose pid is
   * not a real group leader).
   */
  private killWorker(worker: PooledChild<Handle>): void {
    const pid = worker.child.pid;
    if (this.driver.killProcessGroup && typeof pid === 'number' && pid > 0) {
      try {
        process.kill(-pid, 'SIGKILL');
        return;
      } catch {
        // fall through to a single-process kill
      }
    }
    worker.child.kill('SIGKILL');
  }

  /** Record a retire's cause in the counters and label it for the log. */
  private recordRetirement(error: Error): WorkerPoolRetirementReason {
    if (error instanceof WorkerPoolTimeoutError) {
      this.retirements.timeout++;
      return 'timeout';
    }
    if (error instanceof WorkerPoolOomError) {
      this.retirements.oom++;
      return 'oom';
    }
    if (error instanceof WorkerPoolLeaseExpiredError) {
      this.retirements.lease_expired++;
      return 'lease_expired';
    }
    this.retirements.crash++;
    return 'crash';
  }

  /**
   * Reject any in-flight call, SIGKILL the process, drop the child, and — unless
   * draining — spawn a replacement to keep capacity up and drain waiters.
   * Idempotent: the SIGKILL's own `exit` event re-enters here as a no-op.
   *
   * `force` overrides an `onBreach: 'drain'` driver's deferral: when the child is
   * already dead (a crash/exit/fatal) or unreclaimable (an expired lease with no
   * holder left to finish), there is no live session to preserve, so kill now.
   * The non-forced breach paths (RSS-OOM, a voluntary reclaim) respect the policy.
   */
  private retire(worker: PooledChild<Handle>, error: Error, force = false): void {
    if (worker.retired) return;
    // onBreach='drain': don't SIGKILL a live leased session out from under its
    // holder. Defer the kill — the holder keeps using its handle until it
    // release()s, then the lease coordinator applies the retire recorded here.
    if (!force && this.leaseCoordinator.deferRetire(worker, error)) return;
    worker.retired = true;
    this.children.delete(worker);
    const idx = this.idle.indexOf(worker);
    if (idx >= 0) this.idle.splice(idx, 1);

    // A deferred breach (onBreach='drain') carries the original breach reason;
    // prefer it over whatever finally fired the hard retire (a TTL lapse or crash
    // that beat the holder's release), so the kill is classified by root cause —
    // e.g. a deferred RSS-OOM still counts as `oom`, not `crash`.
    const killError = worker.retireOnRelease ?? error;
    worker.retireOnRelease = null;
    const cause = this.recordRetirement(killError);
    if (cause === 'oom') this.rssMonitor.trackImmediateOom(worker);
    this.logger.warn('[WorkerPool] retiring worker', {
      pid: worker.child.pid,
      label: worker.current?.label,
      reason: killError.message,
      cause,
    });

    worker.current?.fail(killError);
    worker.lease = null;
    this.killWorker(worker);

    if (this.draining) return;
    // Replace the lost child if anyone is waiting or we dipped below size.
    if (this.waiters.length > 0 || this.children.size < this.size) {
      this.spawnReplacement();
    }
  }

  /**
   * Refill a lost slot with a fresh child and hand it to a queued waiter. If
   * spawning is itself broken (e.g. a missing/corrupt worker binary), don't
   * leave parked callers hanging — fail them fast rather than retry-storm.
   *
   * This is the single drain point for replacement failures: the spawn runs
   * with `replacement = true`, so its own `failSpawn` won't recurse here, and a
   * failure surfaces through the `catch` below exactly once.
   */
  private spawnReplacement(): void {
    this.spawnChild(true)
      .then((w) => this.release(w))
      .catch((err: unknown) => {
        this.logger.error('[WorkerPool] failed to spawn replacement worker; rejecting waiters', {
          error: err instanceof Error ? err.message : String(err),
          queued: this.waiters.length,
        });
        rejectScheduledWaiters(
          this.waiters,
          new WorkerPoolError(
            `worker spawn failed: ${err instanceof Error ? err.message : String(err)}`
          ),
          this.saturation
        );
      });
  }
}
